// cash (catalog-outline.md "cash"): the till-session aggregate of @radish/core/cash on the wire (stream `till:<id>`).
// Cohort 0 is The Owl Club: one box session per register per night, tip payouts in cash, counted at close. A server
// bank is a floating session a server opens with no float and closes with a closeout a manager approves (W-17, W-63,
// W-64). Event payloads decode to exactly core's `TillEvent` shapes (minus `type`).
import { z } from 'zod';
import { defineCommand } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { additive } from '../../define/upcast.ts';
import { CashCountId, CashMovementId, CloseoutId, DeviceId, EmployeeId, EpochMs, LocalDate, LocationId, Money, TillSessionId } from '../../primitives/codecs.ts';

const CashPurpose = z
  .enum(['paid_in', 'paid_out', 'drop', 'tip_payout', 'gratuity_payout', 'no_sale', 'settle_in', 'settle_out'])
  .meta({ id: 'CashPurpose' });
const TillKind = z.enum(['register', 'server_bank']).meta({ id: 'TillKind' });
const CloseoutFigures = z
  .object({ cashCollected: Money, nonCashTips: Money, gratuity: Money, orderCount: z.int().nonnegative() })
  .meta({ id: 'CloseoutFigures' });
const CloseoutPageFigures = z.object({ cashCollected: Money, nonCashTips: Money, gratuity: Money }).meta({ id: 'CloseoutPageFigures' });
const CloseoutScope = z.enum(['both', 'cash', 'tips']).meta({ id: 'CloseoutScope' });
const CloseoutDestination = z.enum(['cash_box', 'deposit']).meta({ id: 'CloseoutDestination' });
/** `{ "100": 3, "2000": 5 }`: count per denomination, keyed by the denomination in minor units. */
const Denominations = z.record(z.string().regex(/^[1-9][0-9]*$/), z.int().nonnegative()).meta({ id: 'Denominations' });
/** A till letter: `A`, `B`, … optionally numbered (`A2`). */
const Letter = z.string().regex(/^[A-Z](?:[1-9][0-9]*)?$/);

export const TillSnapshot = z
  .object({
    tillSessionId: TillSessionId,
    deviceId: DeviceId,
    status: z.enum(['open', 'closed', 'auto_closed']),
    kind: TillKind,
    letter: z.string(),
    openingFloat: Money,
    businessDate: LocalDate,
    counted: Money.nullable(),
    version: z.int().nonnegative(),
  })
  .meta({ id: 'TillSnapshot' });

/**
 * Every command that answers with a till's snapshot (C8): v2 added `kind` with server banks, and a v1 result described
 * a register drawer. A result recorded with no version may already carry its kind, which wins.
 */
const tillOutput = {
  output: TillSnapshot,
  outputVersion: 2,
  outputUpcastFrom: [{ version: 1, up: (old: unknown) => ({ kind: 'register', ...(old as object) }) }],
} as const;

// ---- errors ----------------------------------------------------------------------------------------------------------
export const TillNotFound = defineError({
  code: 'cash.not_found', httpStatus: 404, message: 'errors.cash.not_found', retryable: false,
  details: z.object({ tillSessionId: TillSessionId }),
});
export const DeviceHasOpenTill = defineError({
  code: 'cash.device_has_open_till', httpStatus: 409, message: 'errors.cash.device_has_open_till', retryable: false,
  details: z.object({ tillSessionId: TillSessionId }),
});
/**
 * The till session id is the import's reserved no-drawer placeholder (`00000000-0000-7000-8000-0000000000b1`, core
 * `IMPORTED_NO_TILL_SESSION_ID`): no till session may be opened on it, or a live payment could pass as one legacy kept
 * no drawer for.
 */
export const ReservedTillSession = defineError({
  code: 'cash.reserved_till_session', httpStatus: 422, message: 'errors.cash.reserved_till_session', retryable: false,
  details: z.object({ tillSessionId: TillSessionId }),
});
/** Any other rule core's till machine refuses (`rule` is core's error code, e.g. `invalid_denominations`). */
export const TillRuleViolation = defineError({
  code: 'cash.rule_violation', httpStatus: 409, message: 'errors.cash.rule_violation', retryable: false,
  details: z.object({ rule: z.string() }),
});

// ---- events ----------------------------------------------------------------------------------------------------------
const till = { aggregate: 'till', deviceConsumed: true } as const;

const TillSessionOpenedPayload = z.object({
  tillSessionId: TillSessionId, mode: z.enum(['box', 'floating']), kind: TillKind, deviceId: DeviceId, ownerEmployeeId: EmployeeId, letter: Letter,
  openingFloat: Money, openedAt: EpochMs, businessDate: LocalDate,
  /** The manager whose PIN approved opening a register drawer for someone without the drawer permission (CD-T13; W-08). */
  approvedBy: EmployeeId.optional(),
});

export const TillSessionOpened = defineEvent({
  ...till,
  name: 'TillSessionOpened',
  version: 3,
  summary: 'A till session was opened: a register drawer with its opening float, or a server bank with none.',
  payload: TillSessionOpenedPayload,
  verifiable: ['tillSessionId', 'deviceId', 'kind', 'openingFloat', 'businessDate'],
  merge: 'lifecycle',
  // v2 added server banks (`kind`, mode `floating`); every v1 session was a register drawer. v3 added the approving
  // manager (`approvedBy`, W-08), absent from every v2 session.
  upcastFrom: [{ version: 1, up: (old) => ({ ...(old as z.input<typeof TillSessionOpenedPayload>), kind: 'register' as const }) }, additive(2)],
  // A node or register that reads v2 (before W-08's approver) gets the session without `approvedBy`, so the cloud, the
  // node and the registers can take v3 in any order.
  downcastTo: [{ version: 2, down: (newer) => withoutOpenApprover(newer) }],
});

/** A TillSessionOpened v3 wire payload as v2 (the same fields without `approvedBy`). */
function withoutOpenApprover(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'approvedBy'));
}

const CashMovementRecordedPayload = z.object({
    movementId: CashMovementId,
    purpose: CashPurpose,
    /** Signed from the session's perspective: money in is positive. */
    amount: Money,
    recipientEmployeeId: EmployeeId.optional(),
    forBusinessDate: LocalDate.optional(),
    voidsMovementId: CashMovementId.optional(),
    /** Settle in/out: the server bank whose closeout the cash settles. */
    counterpartySessionId: TillSessionId.optional(),
    note: z.string().max(500).optional(),
    actorEmployeeId: EmployeeId.optional(),
    overPaidOffline: z.boolean(),
    at: EpochMs,
});

export const CashMovementRecorded = defineEvent({
  ...till,
  name: 'CashMovementRecorded',
  version: 2,
  summary: 'Cash moved in or out of the drawer (paid in/out, drop, tip payout, no-sale, a server-bank settle); an edit is void-and-replace.',
  payload: CashMovementRecordedPayload,
  verifiable: ['movementId', 'purpose', 'amount'],
  merge: 'commutative',
  // The note is free text staff typed and may name people (coordinator 2026-10-15).
  pii: ['note'],
  // v2 added the settle purposes and their counterparty; a v1 movement reads unchanged.
  upcastFrom: [{ version: 1, up: (old) => old as z.input<typeof CashMovementRecordedPayload> }],
});

export const CashCounted = defineEvent({
  ...till,
  name: 'CashCounted',
  version: 1,
  summary: 'The drawer was counted by denomination (an opening, mid-shift or closing count).',
  payload: z.object({ countId: CashCountId, countKind: z.enum(['open', 'mid', 'close']), denominations: Denominations, total: Money }),
  verifiable: ['denominations', 'total'],
  merge: 'commutative',
});

/** A TillSessionClosed v2 wire payload as v1 (the same fields without `closedByEmployeeId`). */
function omitCloser(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'closedByEmployeeId'));
}

/** A TillSessionClosed v3 wire payload as v2 (the same fields without `closedOver`). */
function omitClosedOver(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'closedOver'));
}

/** A TillSessionClosed v4 wire payload as v3 (the same fields without `unsynced`). */
function omitUnsynced(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'unsynced'));
}

/** A TillSessionClosed v5 wire payload as v4 (the same fields without `approvedBy`). */
function omitCloseApprover(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'approvedBy'));
}

/** What a close counted: the orders opened on the drawer, and the payments and refunds taken on it, that its decider held. */
const TillCloseBasis = z.object({
  orderIds: z.array(z.uuid()).max(20_000), tenderIds: z.array(z.uuid()).max(20_000), refundIds: z.array(z.uuid()).max(20_000),
});

export const TillSessionClosed = defineEvent({
  ...till,
  name: 'TillSessionClosed',
  version: 5,
  summary: 'A till session was closed: counted by a person, or auto-closed uncounted at the business-day start.',
  payload: z.object({
    closedAt: EpochMs, counted: Money.optional(), forced: z.boolean(), autoClosed: z.boolean(),
    /** v2 (lane D2): who closed it (legacy `close_employee`, the Drawers dialog's Closed tab); absent when auto-closed or before. */
    closedByEmployeeId: EmployeeId.optional(),
    /**
     * v3 (HUB-37, security review of #1642): what the close counted, as its decider held it. A fact on the drawer outside
     * it landed after the close (the drawer is then amended, cash/internal/late.ts). Absent when the decider did not say
     * (a register's own close, or before v3): the drawer is then amended by any fact on it timed after the close.
     */
    closedOver: TillCloseBasis.optional(),
    /**
     * v4 (HUB-34; Jarrett, plan-docs local-first-offline-mode `architecture.md`:181): the business-day close closed it after
     * its grace ran out while its device still had activity the closing receiver had not received ("unsynced activity").
     * Absent otherwise, and before v4.
     */
    unsynced: z.literal(true).optional(),
    /**
     * v5 (PRM-010; security review of platform #1778, SEC-60): the manager whose PIN approved closing it for someone without
     * the drawer-session permission. Absent when the closer held the permission, and before v5.
     */
    approvedBy: EmployeeId.optional(),
  }),
  // B1687 (lane RG): `forced` and who closed it (the command's employee; absent on an auto-close) are decided the same way at
  // every receiver; an older version's fact is compared as it carries them (sync/verify.ts asCarriedBy). Not the approver.
  verifiable: ['counted', 'forced', 'autoClosed', 'closedByEmployeeId'],
  merge: 'lifecycle',
  // v2 only added the optional closer, v3 the optional basis, v4 the optional unsynced flag and v5 the optional approver: an
  // older payload is a newer one, and a register that reads an older version gets it without them.
  upcastFrom: [additive(1), additive(2), additive(3), additive(4)],
  downcastTo: [
    { version: 4, down: (newer) => omitCloseApprover(newer) }, { version: 3, down: (newer) => omitUnsynced(newer) }, { version: 2, down: (newer) => omitClosedOver(newer) }, { version: 1, down: (newer) => omitCloser(newer) },
  ],
});

export const TillSessionReopened = defineEvent({
  ...till,
  name: 'TillSessionReopened',
  version: 2,
  summary: 'A closed till session was reopened; its earlier counts stay in its history.',
  payload: z.object({
    reopenedAt: EpochMs, reopenedByEmployeeId: EmployeeId.optional(),
    /** The manager whose PIN approved reopening it for someone without the drawer permission (CD-T13; WF-16, L758). */
    approvedBy: EmployeeId.optional(),
  }),
  verifiable: [],
  merge: 'lifecycle',
  // v2 added the approving manager (`approvedBy`), absent from every v1 reopen; a v1 reader gets it without one.
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: (newer) => withoutReopenApprover(newer) }],
});

/** A TillSessionReopened v2 wire payload as v1 (the same fields without `approvedBy`). */
function withoutReopenApprover(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'approvedBy'));
}

/**
 * Who set a counted total: core's `Actor`, the same union labor's corrections use (`LaborActor`). It is its own schema
 * because TillCountCorrected v1 is published with it: pointing it at labor's would change the event's payload and need a
 * v2 (contracts gen refuses it). test/till-actor.test.ts holds the two to the same shape.
 */
export const TillActor = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('staff'), employeeId: EmployeeId }),
    z.object({ kind: z.literal('user'), userId: z.uuid() }),
    z.object({ kind: z.literal('device'), deviceId: DeviceId }),
    z.object({ kind: z.literal('system'), job: z.string().max(64) }),
  ])
  .meta({ id: 'TillActor' });

export const TillCountCorrected = defineEvent({
  ...till,
  name: 'TillCountCorrected',
  version: 1,
  summary: "The back office set a closed register drawer's counted total: what it was (none when auto-closed), what it became, who and when.",
  payload: z.object({ before: Money.optional(), counted: Money, correctedBy: TillActor, correctedAt: EpochMs }),
  verifiable: ['counted'],
  merge: 'lifecycle',
});

export const CloseoutSubmitted = defineEvent({
  ...till,
  name: 'CloseoutSubmitted',
  version: 1,
  summary: 'A server bank submitted its closeout: the cash, tips, gratuity and orders it snapshots for a manager.',
  payload: z.object({
    closeoutId: CloseoutId, figures: CloseoutFigures, isNextDay: z.boolean(), submittedByEmployeeId: EmployeeId.optional(), submittedAt: EpochMs,
  }),
  verifiable: ['closeoutId', 'figures'],
  merge: 'lifecycle',
});

export const CloseoutApproved = defineEvent({
  ...till,
  name: 'CloseoutApproved',
  version: 1,
  summary: "A manager settled a server bank's closeout: cash and tips together, the cash alone, or the tips after the cash.",
  payload: z.object({
    closeoutId: CloseoutId, scope: CloseoutScope, destination: CloseoutDestination, destinationTillSessionId: TillSessionId.optional(),
    /** Signed: positive is cash the server hands the house, negative cash the house pays the server. */
    settled: Money, approvedByEmployeeId: EmployeeId.optional(), approvedAt: EpochMs,
  }),
  verifiable: ['closeoutId', 'scope', 'settled'],
  merge: 'lifecycle',
});

/** Every event the till-session aggregate records, in the order core's `TillEvent` union lists them. */
export const tillEvents = [
  TillSessionOpened, CashMovementRecorded, CashCounted, TillSessionClosed, TillSessionReopened, TillCountCorrected, CloseoutSubmitted, CloseoutApproved,
] as const;

// ---- commands --------------------------------------------------------------------------------------------------------
const onTill = {
  availability: 'location', origin: 'device', stream: { aggregate: 'till', id: (input: { tillSessionId: string }) => input.tillSessionId },
} as const;
const quiet = { risk: 'low', dryRun: false, agent: { expose: false } } as const;
const staff = { permission: { session: 'staff' }, ...tillOutput } as const;

export const OpenTill = defineCommand({
  ...onTill, ...quiet, ...staff,
  name: 'cash.openTill',
  summary: 'Open a till session: a register drawer with an opening float (one per register; needs the drawer permission or a manager PIN approval), or a server bank with none.',
  input: z.object({
    tillSessionId: TillSessionId, locationId: LocationId, deviceId: DeviceId, ownerEmployeeId: EmployeeId, letter: Letter, openingFloat: Money,
    kind: TillKind.optional(),
  }),
  emits: [TillSessionOpened],
  errors: [DeviceHasOpenTill, ReservedTillSession, TillRuleViolation],
  legacy: ['POST /v1/drawers/open'],
});

export const RecordCashMovement = defineCommand({
  ...onTill, ...quiet, ...staff,
  name: 'cash.recordMovement',
  summary: 'Record cash in or out of the drawer: paid in/out, a drop, a tip payout, or a no-sale.',
  input: z.object({
    tillSessionId: TillSessionId, movementId: CashMovementId, purpose: CashPurpose, amount: Money, recipientEmployeeId: EmployeeId.optional(),
    forBusinessDate: LocalDate.optional(), voidsMovementId: CashMovementId.optional(), counterpartySessionId: TillSessionId.optional(),
    note: z.string().max(500).optional(),
  }),
  emits: [CashMovementRecorded],
  errors: [TillNotFound, TillRuleViolation],
  legacy: ['POST /v1/drawers/{id}/cash-management'],
});

export const CountCash = defineCommand({
  ...onTill, ...quiet, ...staff,
  name: 'cash.countCash',
  summary: 'Count the drawer by denomination (an opening or mid-shift count).',
  input: z.object({ tillSessionId: TillSessionId, countId: CashCountId, kind: z.enum(['open', 'mid']), denominations: Denominations }),
  emits: [CashCounted],
  errors: [TillNotFound, TillRuleViolation],
});

export const CloseTill = defineCommand({
  ...onTill, ...staff,
  name: 'cash.closeTill',
  summary: 'Count the drawer and close the till session.',
  input: z.object({ tillSessionId: TillSessionId, countId: CashCountId, denominations: Denominations }),
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [CashCounted, TillSessionClosed],
  errors: [TillNotFound, TillRuleViolation],
  legacy: ['POST /v1/drawers/{id}/close'],
});

/** The business-day close (CD-B04): a till session still open at its business day's start is closed uncounted. */
export const AutoCloseTill = defineCommand({
  availability: 'location', origin: 'any', stream: onTill.stream, ...quiet,
  name: 'cash.autoCloseTill',
  summary: "Close a till session left open at its business day's end, uncounted (the business-day close job).",
  input: z.object({
    tillSessionId: TillSessionId, closeout: z.object({ closeoutId: CloseoutId, figures: CloseoutFigures }).optional(),
    /** HUB-34: closing after the grace with its device's activity still unsynced (the business-day job sets it). */
    unsynced: z.literal(true).optional(),
  }),
  ...tillOutput,
  permission: 'cash.session.manage',
  emits: [CloseoutSubmitted, TillSessionClosed],
  errors: [TillNotFound, TillRuleViolation],
  legacy: ['maintenance:auto-close-drawers'],
});

export const ReopenTill = defineCommand({
  ...onTill, ...staff,
  name: 'cash.reopenTill',
  summary: 'Reopen a closed till session (the audited way to change a closed session).',
  input: z.object({ tillSessionId: TillSessionId }),
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [TillSessionReopened],
  errors: [TillNotFound, TillRuleViolation],
});

/**
 * EP-DRW-12: the back office's Reports › Audit › drawer detail sets a closed register drawer's Actual Closeout Cash (legacy
 * `PUT /v1/drawers/{drawer_id}/total`). Whoever opens the Auditing reports may, as in legacy, where the endpoint needed
 * only a sign-in at the location (FIX: now the permission, and only a closed register drawer, never a negative total).
 */
export const CorrectCountedTotal = defineCommand({
  availability: 'location', origin: 'any', stream: onTill.stream, ...tillOutput,
  name: 'cash.correctCountedTotal',
  summary: "Set a closed register drawer's counted total (Actual Closeout Cash) from the back office.",
  input: z.object({ tillSessionId: TillSessionId, counted: Money }),
  permission: 'reports.auditing.view',
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [TillCountCorrected],
  errors: [TillNotFound, TillRuleViolation],
  legacy: ['PUT /v1/drawers/{drawer_id}/total'],
});

export const SubmitCloseout = defineCommand({
  ...onTill, ...staff,
  name: 'cash.submitCloseout',
  summary: "Submit a server bank's closeout (cash, tips, gratuity, orders) for a manager and close the bank.",
  input: z.object({
    tillSessionId: TillSessionId, closeoutId: CloseoutId,
    /** What legacy's API records: the gross cash payments, the tips less the bank's tip payouts (can be negative), the gratuity. */
    figures: CloseoutFigures,
    /** The closeout page's figures the register sends, which the guards read (absent from an older register). */
    pageFigures: CloseoutPageFigures.optional(),
  }),
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [CloseoutSubmitted, TillSessionClosed],
  errors: [TillNotFound, TillRuleViolation],
  legacy: ['POST /v1/drawer-closure-intent'],
});

export const ApproveCloseout = defineCommand({
  ...onTill, ...staff,
  name: 'cash.approveCloseout',
  summary: "Settle a server bank's closeout: cash and tips together, the cash alone, or the tips after the cash.",
  input: z.object({
    tillSessionId: TillSessionId, scope: CloseoutScope, destination: CloseoutDestination, destinationTillSessionId: TillSessionId.optional(),
  }),
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [CloseoutApproved],
  errors: [TillNotFound, TillRuleViolation],
  legacy: ['PUT /v1/drawer-closure-intent/{id}', 'PUT /v1/drawer-closure-intent/separate-tipping/{id}', 'PUT /v1/drawer-closure-intent/tip-payout/{id}'],
});
